/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import { useParams } from "@packages/framework";
import type { BibleManifest, BibleSearchableJson } from "@packages/sdk";
import { bookIdSchema } from "@packages/sdk";
import {
  CloseIcon,
  cssMediaQueryMinWidthTablet,
  fontSizes,
  IconButton,
  lineHeights,
  spacing,
  useTheme,
  zIndices,
} from "@packages/ui";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@packages/ui/src/components/_base/shadcn/popover.emotion";
import { PopoverClose } from "@radix-ui/react-popover";
import type { MouseEventHandler, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { CrossReference } from "./CrossReference";
import { getText } from "./select.utilities";

const popoverStyle: Interpolation = {
  zIndex: zIndices.modalOverModal,
};

export const BibleNotes = ({
  note,
  manifest,
  searchableJson,
}: {
  note: ReactNode;
  manifest: BibleManifest;
  searchableJson: BibleSearchableJson;
}) => {
  const { translation: translationName } = useParams();
  const [open, setOpen] = useState(!!note);
  const [crossRefs, setCrossRefs] = useState<ReactNode | null>(null);
  const { themeContainerRef } = useTheme();
  const t = useTranslations();
  const [maxLines, setMaxLines] = useState<number>(20);
  const [appExists, setAppExists] = useState<boolean>(false);

  useEffect(() => {
    setAppExists(!!themeContainerRef.current);
  }, []);

  useEffect(() => {
    if (note) setOpen(true);
    else setOpen(false);
  }, [note]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const determineMaxLines = () => {
        // max-height: 90vh, 20px line-height for medium text, -1 for title of popup
        setMaxLines(
          (window.innerHeight * 0.9) / parseInt(lineHeights.bodyM) - 1,
        );
      };

      determineMaxLines();

      window.addEventListener("resize", determineMaxLines);

      return () => {
        window.removeEventListener("resize", determineMaxLines);
      };
    }
  }, []);

  const maxCharacters = useMemo(() => {
    // 1em = ~font size
    const em = parseInt(fontSizes.bodyM);
    // max-width: 300, but most things are smaller than 1em, so let's do 1.5
    return (450 / em) * maxLines;
  }, [maxLines]);

  const handleClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.stopPropagation();
      const source = e.target as HTMLElement;
      const link = source.closest("a");
      if (link) {
        e.preventDefault();
        if (link.href) {
          // ignore the link that takes us back to where we opened the note
          return;
        }
        // ignore the link that is only describing the passage the note relates to
        const noteParent = link.closest(".note-text");
        if (!noteParent || link === noteParent.querySelector("a")) return;

        const crossReference = link.dataset.reference?.split(":");
        if (!crossReference?.[0]) return;
        const { data: bookId } = bookIdSchema.safeParse(crossReference[0]);
        if (!bookId) return;
        const manifestBook = bookId
          ? manifest.books.find((bk) => bk.id === bookId)
          : null;
        const bookName = manifestBook
          ? (manifestBook.name_in_chapter_title ?? manifestBook.name)
          : null;
        if (!bookName) return;
        if (crossReference?.length === 3) {
          // check for additional verses
          const rangeSplit = link.dataset.type?.split(/[-—–]/);
          if (rangeSplit?.length > 1) {
            let startChapter = crossReference[1];
            let startVerse = crossReference[2];
            let endChapter: string, endVerse: string;
            const endReference = rangeSplit[1];
            if (endReference.includes(":")) {
              // it's in another chapter
              [endChapter, endVerse] = endReference.split(":");
            } else {
              // same chapter only
              endChapter = startChapter;
              endVerse = endReference;
            }
            const nStartChapter = parseInt(startChapter);
            const nStartVerse = parseInt(startVerse);
            let nEndChapter = parseInt(endChapter);
            let nEndVerse = parseInt(endVerse);

            const results = getText({
              searchableJson,
              maxCharacters,
              nStartChapter,
              nEndChapter,
              nEndVerse,
              nStartVerse,
              book: bookId,
            });
            if (!results) return;
            const { text, shortened, endedEarlyAtVerse, endedEarlyAtChapter } =
              results;

            setCrossRefs(
              <CrossReference
                title={`${bookName} ${startChapter}:${startVerse}-${nEndChapter > nStartChapter ? `${endChapter}:` : ""}${endVerse}`}
                text={text}
              >
                {shortened ? (
                  <a
                    href={`/bible/${translationName}/${bookId}/${endedEarlyAtChapter}/${endedEarlyAtVerse}`}
                  >
                    {t("read_more")}
                  </a>
                ) : null}
              </CrossReference>,
            );
            return;
          }
          const { text } = getText({
            searchableJson,
            maxCharacters,
            nStartChapter: parseInt(crossReference[1]),
            nStartVerse: parseInt(crossReference[2]),
            nEndVerse: parseInt(crossReference[2]),
            book: bookId,
          });
          setCrossRefs(
            <CrossReference
              title={`${bookName} ${crossReference[1]}:${crossReference[2]}`}
              text={text}
            />,
          );
        } else if (crossReference?.length === 2) {
          const { text, shortened, endedEarlyAtVerse, endedEarlyAtChapter } =
            getText({
              searchableJson,
              maxCharacters,
              nStartChapter: parseInt(crossReference[1]),
              book: bookId,
            });
          setCrossRefs(
            <CrossReference
              title={`${bookName} ${crossReference[1]}`}
              text={text}
            >
              {shortened ? (
                <a
                  href={`/bible/${translationName}/${bookId}/${endedEarlyAtChapter}/${endedEarlyAtVerse}`}
                >
                  {t("read_more")}
                </a>
              ) : null}
            </CrossReference>,
          );
        }
      }
    },
    [searchableJson, maxCharacters, translationName, t],
  );

  if (!appExists) return null;

  return (
    <>
      <Popover key="note" open={open} onOpenChange={setOpen} modal>
        {createPortal(
          <PopoverAnchor css={{ position: "fixed", bottom: 0, left: "50%" }} />,
          themeContainerRef.current,
          "bible-note",
        )}
        {note ? (
          <PopoverContent
            side={"top"}
            onClick={handleClick}
            css={{
              maxWidth: 300,
              zIndex: zIndices.modalOverModal,
              [cssMediaQueryMinWidthTablet]: { maxWidth: 450 },
            }}
          >
            <PopoverClose asChild>
              <IconButton
                icon={<CloseIcon />}
                variant="transparentNeutral"
                size="s"
                css={{ justifySelf: "flex-end" }}
              />
            </PopoverClose>
            {note}
          </PopoverContent>
        ) : null}
      </Popover>
      <Popover
        key="cross-reference"
        open={!!crossRefs}
        onOpenChange={(open) => {
          if (!open) setCrossRefs(null);
        }}
      >
        {createPortal(
          <PopoverAnchor css={{ position: "fixed", right: 0, top: "50%" }} />,
          themeContainerRef.current,
          "bible-cross-refs",
        )}
        {crossRefs ? (
          <PopoverContent
            side={"left"}
            sideOffset={25}
            css={{
              paddingBlock: spacing.m,
              maxWidth: 300,
              maxHeight: "90vh",
              zIndex: zIndices.modalOverModal,
            }}
          >
            <PopoverClose asChild>
              <IconButton
                icon={<CloseIcon />}
                variant="transparentNeutral"
                size="s"
                css={{
                  justifySelf: "flex-end",
                  position: "absolute",
                  top: spacing.s,
                }}
              />
            </PopoverClose>
            {crossRefs}
          </PopoverContent>
        ) : null}
      </Popover>
    </>
  );
};
